/**
 * AI committee reasoning.
 *
 * Shows each agent ("bot") as an expandable card — its score, its findings,
 * the evidence behind them, the risks it flags, and the research action it
 * suggests — and then the committee's blended final decision. This is the
 * "what each bot is thinking, then the final call" view: the deliberation is
 * shown, not averaged away into a single number.
 */

import React, { useState } from "react";
import type { ResearchReport } from "@/types";

interface AgentOpinion {
  agent_name: string;
  component: string;
  label: string;
  score: number;
  confidence: number;
  findings: string[];
  evidence: string[];
  risks: string[];
  suggested_action: string;
}

const scoreColor = (score: number): string => {
  if (score >= 70) return "text-emerald-400";
  if (score >= 55) return "text-signal-teal";
  if (score >= 45) return "text-mist-200";
  if (score >= 30) return "text-signal-amber";
  return "text-rose-400";
};

const scoreBarColor = (score: number): string => {
  if (score >= 70) return "bg-emerald-400";
  if (score >= 55) return "bg-signal-teal";
  if (score >= 45) return "bg-mist-400";
  if (score >= 30) return "bg-signal-amber";
  return "bg-rose-400";
};

const asList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === "string");
  if (typeof value === "string" && value.trim()) return [value];
  return [];
};

function AgentCard({ opinion }: { opinion: AgentOpinion }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const score = Number(opinion.score) || 0;
  const confidence = Number(opinion.confidence) || 0;
  const findings = asList(opinion.findings);
  const evidence = asList(opinion.evidence);
  const risks = asList(opinion.risks);
  const headline = findings[0] ?? opinion.suggested_action ?? "";

  return (
    <div className="rounded-lg border border-ink-700 bg-ink-900/50">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span className={`w-10 shrink-0 text-lg font-semibold ${scoreColor(score)}`}>
          {score.toFixed(0)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-mist-100">{opinion.label}</span>
          {headline && !open && (
            <span className="block truncate text-xs text-mist-400">{headline}</span>
          )}
        </span>
        <span className="shrink-0 text-xs text-mist-500">
          {(confidence * 100).toFixed(0)}% conf.
        </span>
        <span className="shrink-0 text-mist-500">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-ink-800 px-4 py-3 text-sm">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink-800">
            <div
              className={`h-full origin-left animate-bar-grow ${scoreBarColor(score)}`}
              style={{
                width: `${Math.max(0, Math.min(100, score))}%`,
                boxShadow: `0 0 8px ${
                  score >= 70
                    ? "rgba(52, 211, 153, 0.3)"
                    : score >= 55
                      ? "rgba(47, 211, 166, 0.3)"
                      : score >= 30
                        ? "rgba(242, 178, 92, 0.3)"
                        : "rgba(239, 110, 110, 0.3)"
                }`,
              }}
            />
          </div>

          {findings.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-mist-500">Findings</p>
              <ul className="mt-1 space-y-1 text-mist-200">
                {findings.map((item, i) => (
                  <li key={i}>• {item}</li>
                ))}
              </ul>
            </div>
          )}

          {evidence.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-mist-500">Evidence</p>
              <ul className="mt-1 space-y-1 text-mist-300">
                {evidence.map((item, i) => (
                  <li key={i}>• {item}</li>
                ))}
              </ul>
            </div>
          )}

          {risks.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-mist-500">
                Risks it flags
              </p>
              <ul className="mt-1 space-y-1 text-signal-amber/90">
                {risks.map((item, i) => (
                  <li key={i}>• {item}</li>
                ))}
              </ul>
            </div>
          )}

          {opinion.suggested_action && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-mist-500">
                This agent's call
              </p>
              <p className="mt-1 text-mist-200">{opinion.suggested_action}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AgentReasoningPanel({
  report,
}: {
  report: ResearchReport;
}): React.ReactElement | null {
  const results = report.agent_results as
    | { committee?: { opinions?: AgentOpinion[]; overall_action?: string } }
    | undefined;
  const opinions = results?.committee?.opinions ?? [];
  const overallAction = results?.committee?.overall_action ?? "";
  const overall = Number(report.overall_score);

  if (opinions.length === 0) return null;

  // Show the most opinionated agents first (furthest from a neutral 50).
  const ordered = [...opinions].sort(
    (a, b) => Math.abs((Number(b.score) || 0) - 50) - Math.abs((Number(a.score) || 0) - 50),
  );

  return (
    <div className="panel p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-mist-100">
          The committee — what each agent thinks
        </h2>
        <span className="text-sm text-mist-400">{opinions.length} agents deliberated</span>
      </div>
      <p className="mt-1 text-sm text-mist-400">
        Tap any agent to see its reasoning, the evidence behind it, and the risks it raised.
      </p>

      <div className="mt-4 space-y-2">
        {ordered.map((opinion) => (
          <AgentCard key={`${opinion.agent_name}-${opinion.component}`} opinion={opinion} />
        ))}
      </div>

      <div className="mt-5 rounded-lg border border-signal-teal/30 bg-signal-teal/5 p-4 glow-border">
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-sm font-semibold text-mist-100">Final committee decision</p>
          <span className={`text-2xl font-bold ${scoreColor(overall)} animate-count-glow`}>
            {Number.isFinite(overall) ? overall.toFixed(0) : "—"}
            <span className="text-sm font-normal text-mist-500"> / 100</span>
          </span>
        </div>
        {overallAction && <p className="mt-2 text-sm text-mist-200">{overallAction}</p>}
        {report.executive_summary && (
          <p className="mt-3 text-sm leading-relaxed text-mist-300">{report.executive_summary}</p>
        )}
        <p className="mt-3 text-xs text-mist-500">
          Research guidance only — what to investigate next, not a trade instruction or a guarantee.
        </p>
      </div>
    </div>
  );
}
